
import { Injectable } from '@angular/core';
import { QueryEntity } from '@datorama/akita';
import { Observable } from 'rxjs';

import { FeedbackStateEnum } from 'src/app/api/models/feedback/feedback.enum';
import { SystemUsage, UnverifiedSystem } from 'src/app/api/models/systems/systems.interface';
import { FilterCategory, FilterOption } from 'src/app/core/models/filtering.interface';
import { MineSort } from 'src/app/shared/mine-sort/mine-sort.interface';
import { SortDirectionEnum } from 'src/app/shared/sort-direction.enum';
import { CategoriesFilterOptions, EmployeesCountFilterOptions, GeneralFilterOptions, LastLoginFilterOptions, OriginFilterOptions, RadarFilterGroups } from '../radar-table/radar-filters/radar-filters.enum';
import { RadarColumnKeys } from '../radar-table/radar-table.enum';
import { ConnectedEmployeesHelper } from '../shared/connected-employees/connected-employees.helper';
import { SystemUsageRate } from '../shared/system-usage/system-usage.enum';
import { SystemUsageHelper } from '../shared/system-usage/system-usage.helper';
import { UnverifiedSystemsState, UnverifiedSystemsStore } from './unverified-systems.store';
import { DataSourceOriginEnum } from 'src/app/api/models/systems/systems.enum';
import { FeatureFlags } from 'src/app/api/models/profile/profile-feature-flags.enum';
import { FeatureFlagQuery } from 'src/app/feature-flag/state/feature-flag.query';

const SPECIAL_ORIGIN_OPTIONS = {
	[OriginFilterOptions.OnlyEmail]: OriginFilterOptions.Email,
};

// "Within last N days" last-login buckets. Hoisted to module scope so it isn't
// rebuilt per entity (filterByLastLogin runs once per row, up to 850 rows).
const WITHIN_DAYS_MAP: Record<string, number> = {
	[LastLoginFilterOptions.Last7Days]: 7,
	[LastLoginFilterOptions.Last30Days]: 30,
	[LastLoginFilterOptions.Last60Days]: 60,
};

const CORE_SYSTEMS_EMPLOYEES_COUNT_THRESHOLD = 5;

@Injectable({ 
	providedIn: 'root' 
})
export class UnverifiedSystemsQuery extends QueryEntity<UnverifiedSystemsState> {

	constructor(
		protected store: UnverifiedSystemsStore,
		private featureFlagQuery: FeatureFlagQuery
	) {
		super(store);
	}

	selectSystemsByFiltersAndSearchTerm(activeFilters: FilterCategory[], searchTerm: string, sort: MineSort = null, listFilter?: any): Observable<UnverifiedSystem[]> {
		// no filters & search term && sort
		if (activeFilters.length === 0 && searchTerm === '' && !sort) {
			return this.selectAll();
		}
		const selectedUsages = activeFilters.find(f => f.id === RadarFilterGroups.Usage)?.options ?? [];
		const selectedEmployeesCount = activeFilters.find(f => f.id === RadarFilterGroups.EmployeesCount)?.options ?? [];
		const selectedCategories = activeFilters.find(f => f.id === RadarFilterGroups.Category)?.options ?? [];
		const selectedFeedbacksStatus = activeFilters.find(f => f.id === RadarFilterGroups.General)?.options ?? [];
		const selectedOrigin = activeFilters.find(f => f.id === RadarFilterGroups.Origin)?.options ?? [];
		const selectedLastLogin = activeFilters.find(f => f.id === RadarFilterGroups.LastLogin)?.options ?? [];
		const selectedRecommended = listFilter === GeneralFilterOptions.Recommended;
		const selectedUknown = listFilter === GeneralFilterOptions.Unknown;

		// has filters or search term
		return this.selectAll({
			filterBy: [
				entity => this.filterByUsage(entity.systemUsage?.discoveryScore ?? 0, selectedUsages) && // filter by usage
						  this.filterByEmployeesCount(entity.connectedAccounts ?? 0, selectedEmployeesCount) && // filter by employees count
						  this.filterByCategories(entity.category, selectedCategories) && // filter by category
						  this.filterByFeedback(entity.generalFeedbackState, selectedFeedbacksStatus) &&
						  this.filterByOrigin(entity.dataSourceOrigin, selectedOrigin) &&
						  this.filterByLastLogin(entity.appLastLogin, selectedLastLogin) &&
						  this.filterByRecommended(entity, selectedRecommended) &&
						  this.filterBySearchTerm(entity.name, searchTerm) &&
						  this.filterBySystemType(entity.systemType, listFilter, selectedUknown)
			],
			sortBy: (s1, s2) => sort?.active === RadarColumnKeys.DiscoveryDate ? this.sortByDiscoveryDate(s1, s2, sort.direction) :
								sort?.active === RadarColumnKeys.FeedbackStatus ? this.sortByFeedbackStatus(s1, s2, sort.direction) :
								sort?.active === RadarColumnKeys.Category ? this.sortByCategory(s1, s2, sort.direction) :
								sort?.active === RadarColumnKeys.DataSource ? this.sortByDataSourceName(s1, s2, sort.direction) :
								sort?.active === RadarColumnKeys.Employees ? this.sortByConnectedEmployees(s1, s2, sort.direction) :
								sort?.active === RadarColumnKeys.Usage ? this.sortByUsage(s1, s2, sort.direction) :
								sort?.active === RadarColumnKeys.DataSourceOrigin ? this.sortByDataSourceOrigin(s1, s2, sort.direction) :
								sort?.active === RadarColumnKeys.LastLogin ? this.sortByLastLogin(s1, s2, sort.direction) : 1,
		});
	}

	private sortByLastLogin(systemA: UnverifiedSystem, systemB: UnverifiedSystem, direction: SortDirectionEnum): number {
		if (direction === SortDirectionEnum.Default) {
			return 0;
		}
		const aTime = systemA.appLastLogin ? new Date(systemA.appLastLogin).getTime() : null;
		const bTime = systemB.appLastLogin ? new Date(systemB.appLastLogin).getTime() : null;
		if (aTime === null && bTime === null) return 0;
		if (aTime === null) return 1;
		if (bTime === null) return -1;
		return direction === SortDirectionEnum.Asc ? aTime - bTime : bTime - aTime;
	}

	private filterByLastLogin(appLastLogin: string | null | undefined, selectedOptions: FilterOption[]): boolean {
		if (selectedOptions.length === 0) {
			return true;
		}
		// Never-logged-in systems ("None"/"N/A", no appLastLogin) have no timestamp to
		// compare against, so they are excluded from every last-login bucket — including
		// "More than 60 days ago".
		if (!appLastLogin) {
			return false;
		}
		const loginTime = new Date(appLastLogin).getTime();
		const now = Date.now();
		const dayMs = 86_400_000;
		return selectedOptions.some(opt => {
			if (opt.id === LastLoginFilterOptions.Over60Days) {
				return loginTime < now - 60 * dayMs;
			}
			const days = WITHIN_DAYS_MAP[opt.id];
			return days != null && loginTime >= now - days * dayMs;
		});
	}

	private sortByDiscoveryDate(systemA: UnverifiedSystem, systemB: UnverifiedSystem, direction: SortDirectionEnum): number {
		return direction === SortDirectionEnum.Default ? 0 : 
			   direction === SortDirectionEnum.Asc ? 
			systemA.discoveryDate ? new Date(systemA.discoveryDate).getTime() - new Date(systemB.discoveryDate).getTime() : -1 :
			systemB.discoveryDate ? new Date(systemB.discoveryDate).getTime() - new Date(systemA.discoveryDate).getTime() : -1;
	}

	private sortByFeedbackStatus(systemA: UnverifiedSystem, systemB: UnverifiedSystem, direction: SortDirectionEnum): number {
		return direction === SortDirectionEnum.Default ? 0 : 
			   direction === SortDirectionEnum.Asc ? 
			systemA.generalFeedbackState.localeCompare(systemB.generalFeedbackState) :
			systemB.generalFeedbackState.localeCompare(systemA.generalFeedbackState);
	}

	private sortByDataSourceOrigin(systemA: UnverifiedSystem, systemB: UnverifiedSystem, direction: SortDirectionEnum): number {
		return direction === SortDirectionEnum.Default ? 0 : 
			   direction === SortDirectionEnum.Asc ? 
			systemA.dataSourceOrigin[0]?.localeCompare(systemB.dataSourceOrigin[0]) :
			systemB.dataSourceOrigin[0]?.localeCompare(systemA.dataSourceOrigin[0]);
	}

	private sortByCategory(systemA: UnverifiedSystem, systemB: UnverifiedSystem, direction: SortDirectionEnum): number {
		return direction === SortDirectionEnum.Default ? 0 : 
			   direction === SortDirectionEnum.Asc ? 
			systemA.category?.localeCompare(systemB.category) :
			systemB.category?.localeCompare(systemA.category);
	}

	private sortByDataSourceName(systemA: UnverifiedSystem, systemB: UnverifiedSystem, direction: SortDirectionEnum): number {
		return direction === SortDirectionEnum.Default ? 0 : 
			   direction === SortDirectionEnum.Asc ? 
			systemA.name?.localeCompare(systemB.name) :
			systemB.name?.localeCompare(systemA.name);
	}

	private sortByConnectedEmployees(systemA: UnverifiedSystem, systemB: UnverifiedSystem, direction: SortDirectionEnum): number {
		if (direction === SortDirectionEnum.Default) {
			return 0;
		}

		const aConnected = systemA.connectedAccounts ?? Number.NEGATIVE_INFINITY;
		const bConnected = systemB.connectedAccounts ?? Number.NEGATIVE_INFINITY;
		return direction === SortDirectionEnum.Asc ? (aConnected - bConnected) : (bConnected - aConnected);
	}

	private sortByUsage(systemA: UnverifiedSystem, systemB: UnverifiedSystem, direction: SortDirectionEnum): number {
		if (direction === SortDirectionEnum.Default) {
			return 0;
		}

		const aUsage = systemA.systemUsage?.discoveryScore ?? Number.NEGATIVE_INFINITY;
		const bUsage = systemB.systemUsage?.discoveryScore ?? Number.NEGATIVE_INFINITY;
		return direction === SortDirectionEnum.Asc ? (aUsage - bUsage) : (bUsage - aUsage);
	}

	private filterByRecommended(entity: UnverifiedSystem, selected: boolean): boolean {
		if (!selected) {
			return true;
		}
		if(this.featureFlagQuery.getFlag(FeatureFlags.DevRopaCoreSystems)) {
			return this.matchesFilterCondition(entity);	
		}
		else {
			return this.isRecommeded(entity.dataSourceOrigin, entity.systemUsage, entity.connectedAccounts);
		}
	}

	private filterByFeedback(feedbackStatus: FeedbackStateEnum, selected: FilterOption[]): boolean {
		if (selected.length === 0) {
			return true;
		}
		return selected.filter(option => option.id === feedbackStatus)?.length > 0;

	}

	private filterByUsage(usageScore: number, selectedUsages: FilterOption[]): boolean {
		if (selectedUsages.length === 0) {
			return true;
		}

		const convertedUsageScore = SystemUsageHelper.convertScoreToUsageEnum(usageScore);
		return selectedUsages.filter(option => option.id === convertedUsageScore)?.length > 0;
	}

	private filterByOrigin(origins: DataSourceOriginEnum[], selected: FilterOption[]): boolean {
		const originOptions = origins.reduce((options, origin) => {
			const optionsForOrigin = this.updateOriginToFilteringTypes(origin);
			return [origin, ...options, ...optionsForOrigin];
		},[] as string[]);

		const regularOriginIds: string[] = selected.map(({ id }) => id);
		const exclusiveOriginIds: string[] = selected.filter(({ id }) => SPECIAL_ORIGIN_OPTIONS[id]).map(({ id }) => SPECIAL_ORIGIN_OPTIONS[id]);
		const hasRegularSelection = regularOriginIds.some(id => originOptions.find(option => option.includes(id)));
		const hasExclusiveSelection = exclusiveOriginIds.some(id => originOptions.find(option => option.includes(id))) && 
			!hasRegularSelection &&
			origins.length === 1;

		return selected.length === 0 ||
			hasRegularSelection ||
			hasExclusiveSelection;
	}

	private filterByEmployeesCount(connectedAccounts: number, selectedEmployeesCount: FilterOption[]): boolean {
		if (selectedEmployeesCount.length === 0) {
			return true;
		}

		const convertedConnectedAccounts = ConnectedEmployeesHelper.convertEmployeesCountToEnum(connectedAccounts);
		return selectedEmployeesCount.filter(option => option.id === convertedConnectedAccounts)?.length > 0;
	}

	private filterByCategories(category: string, selectedCategories: FilterOption[]): boolean {
		if (selectedCategories.length === 0) {
			return true;
		}

		return selectedCategories.filter(option => option.label === category)?.length > 0;
	}

	private filterBySearchTerm(systemName: string, searchTerm: string): boolean {
		if (searchTerm === '') {
			return true;
		}

		return systemName?.toLowerCase()?.startsWith(searchTerm?.toLowerCase());
	}

	private filterBySystemType(systemType: string, systemTypeToFilter: string, selected: boolean): boolean {
		if (!selected) {
			return true;
		}
		return systemType === systemTypeToFilter;
	}

	selectCountCategory(categoryName: string): Observable<number> {
		return this.selectCount( ({category}) => category === categoryName);
	}

	selectCountOrigin(origin: DataSourceOriginEnum): Observable<number> {
		return this.selectCount( ({dataSourceOrigin} ) => {
			return dataSourceOrigin.some(dsOrigin => this.updateOriginToFilteringTypes(origin).includes(dsOrigin));
		});
	}

	selectCountSystemType(systemTypeToCount: string): Observable<number> {
		return this.selectCount( ({systemType}) => systemType === systemTypeToCount);
	}

	private updateOriginToFilteringTypes(origin: DataSourceOriginEnum): DataSourceOriginEnum[] {
		switch (origin) {
			case DataSourceOriginEnum.Email:
				return [DataSourceOriginEnum.Email];
			case DataSourceOriginEnum.Sso:
				return [DataSourceOriginEnum.Sso, DataSourceOriginEnum.GoogleSso, DataSourceOriginEnum.OktaSso, DataSourceOriginEnum.EntraSso];
			case DataSourceOriginEnum.Cloud:
				return [DataSourceOriginEnum.Cloud, DataSourceOriginEnum.AwsCloud, DataSourceOriginEnum.GcpCloud];
			default:
				return [origin]; 
		}
	}
	
	selectCountRecommended(): Observable<number> {
		if (!this.featureFlagQuery.getFlag(FeatureFlags.DevRopaCoreSystems)) {
			return this.selectCount( ({dataSourceOrigin, systemUsage, connectedAccounts }) => this.isRecommeded(dataSourceOrigin, systemUsage, connectedAccounts));
		} else {
			return this.selectCount( (entity) => this.matchesFilterCondition(entity));
		}
	}
	
	getCountRecommended(): number {
		if (!this.featureFlagQuery.getFlag(FeatureFlags.DevRopaCoreSystems)) {
			return this.getCount(({ dataSourceOrigin, systemUsage, connectedAccounts }) => this.isRecommeded(dataSourceOrigin, systemUsage, connectedAccounts));
		} else {
			return this.getCount((entity) => this.matchesFilterCondition(entity));
		}
	}

	private isRecommeded(dataSourceOrigin: DataSourceOriginEnum[], systemUsage: SystemUsage, connectedAccounts: number): boolean {
		const originsToInclude: string[] = Object.values(OriginFilterOptions);

		return originsToInclude.some((origin: DataSourceOriginEnum) => dataSourceOrigin.includes(origin) && (
			!systemUsage || SystemUsageHelper.convertScoreToUsageEnum(systemUsage.discoveryScore) === SystemUsageRate.High ||
			!connectedAccounts || ConnectedEmployeesHelper.convertEmployeesCountToEnum(connectedAccounts) ===  EmployeesCountFilterOptions.Many
		));
	}

	selectCountUsage(usage: SystemUsageRate): Observable<number> {
		return this.selectCount( ({ systemUsage }) => SystemUsageHelper.convertScoreToUsageEnum(systemUsage.discoveryScore) === usage);
	}

	selectCountEmployees(employeesCount: EmployeesCountFilterOptions): Observable<number> {
		return this.selectCount( ({ connectedAccounts }) => ConnectedEmployeesHelper.convertEmployeesCountToEnum(connectedAccounts) === employeesCount);
	}

	selectCountWithFeedback(): Observable<number> {
		return this.selectCount( ({ generalFeedbackState }) => [FeedbackStateEnum.Pending, FeedbackStateEnum.Responded].includes(generalFeedbackState));
	}

	getMany(systemsIds: string[]): UnverifiedSystem[] {
		const systemsIdsSet = new Set(systemsIds);
		return this.getAll({
			filterBy: ({ systemId }) => systemsIdsSet.has(systemId),
		});
	}

	selectCoreSystems(): Observable<UnverifiedSystem[]> {
		return this.selectAll({
			filterBy: [
				entity => this.matchesFilterCondition(entity)
			]
		});
	}

	private matchesFilterCondition(system: UnverifiedSystem): boolean {
		const hasMoreThan20Employees = (system.connectedAccounts ?? 0) > CORE_SYSTEMS_EMPLOYEES_COUNT_THRESHOLD;
		const isNotUnknown = system.systemType !== GeneralFilterOptions.Unknown;
		const hasSSODiscovery = this.hasSSODiscoveryMethod(system.dataSourceOrigin ?? []);

		if (!hasMoreThan20Employees || !isNotUnknown) {
			return false;
		}

		// Condition 1: More than 20 employees AND SSO discovery method AND NOT unknown system
		if (hasSSODiscovery) {
			return true;
		}

		// Condition 2: More than 20 employees AND NOT SSO discovery AND industry IS NOT (social media, travel, ecommerce, health/wealth/fitness) AND NOT unknown systems
		const excludedCategories = [
			CategoriesFilterOptions.SocialMedia,
			CategoriesFilterOptions.Travel,
			CategoriesFilterOptions.Ecommerce,
			CategoriesFilterOptions.HealthWellnessFitness
		];
		const isExcludedCategory = system.category && excludedCategories.some(excluded => excluded === system.category);
		
		return !isExcludedCategory;
	}

	private hasSSODiscoveryMethod(dataSourceOrigin: DataSourceOriginEnum[]): boolean {
		const ssoMethods = [
			DataSourceOriginEnum.Sso,
			DataSourceOriginEnum.GoogleSso,
			DataSourceOriginEnum.OktaSso,
			DataSourceOriginEnum.EntraSso
		];
		return dataSourceOrigin.some(origin => ssoMethods.includes(origin));
	}

	isNameInInventory(name: string): boolean {
		return this.getAll({
			filterBy: [
				entity => entity.name?.toLowerCase() === name?.toLowerCase()
			]
		}).length > 0;
	}
}
